import { NavLink, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import { useOrders } from "@/context/OrdersContext";
import { useNotifications } from "@/context/NotificationsContext";
import { roleMeta } from "@/config/roles";
import {
  EMPTY_PERMISSIONS,
  canViewDashboard,
  canViewMenu,
  canViewOrders,
  canViewSettings,
} from "@/config/permissions";

const ICON_URL =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/8a7ae8c47f80a7ae4dacefc8dacd2c1f.png";

interface NavItem {
  key: string;
  label: string;
  icon: string;
  to: string;
  show: boolean;
  badge?: number;
}

interface BranchSidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

function SidebarContent({ onNavigate }: { onNavigate: () => void }) {
  const navigate = useNavigate();
  const { employee, logout } = useBranch();
  const { unreadNumbers } = useOrders();
  const { unreadCount: unreadNotifications } = useNotifications();
  const role = roleMeta(employee?.role ?? "cashier");
  const permissions = employee?.permissions ?? EMPTY_PERMISSIONS;

  const items: NavItem[] = [
    { key: "dashboard", label: "الرئيسية", icon: "ri-home-5-line", to: "/dashboard", show: canViewDashboard(permissions) },
    { key: "orders", label: "الطلبات", icon: "ri-file-list-3-line", to: "/orders", show: canViewOrders(permissions), badge: unreadNumbers.length },
    { key: "pos", label: "شاشة الكاشير", icon: "ri-computer-line", to: "/pos", show: permissions.pos },
    { key: "menu", label: "المنيو", icon: "ri-restaurant-2-line", to: "/menu", show: canViewMenu(permissions) },
    { key: "reports", label: "التقارير", icon: "ri-bar-chart-2-line", to: "/reports", show: permissions.reports },
    { key: "settings", label: "إعدادات الفرع", icon: "ri-settings-3-line", to: "/settings", show: canViewSettings(permissions) },
    { key: "notifications", label: "الإشعارات", icon: "ri-notification-3-line", to: "/notifications", show: true, badge: unreadNotifications },
    { key: "account", label: "حسابي", icon: "ri-user-3-line", to: "/account", show: true },
  ];

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <>
      <div className="px-5 pt-6 pb-5 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src={ICON_URL} alt="Your Cashier" className="w-7 h-7 object-contain" />
          </div>
          <span className="font-bold text-base tracking-tight">Your Cashier</span>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-white">{employee?.branchName}</p>
          <p className="text-xs text-white/45 mt-1">{employee?.storeName}</p>
        </div>

        <span
          className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[11px] font-bold ${role.badgeClass}`}
        >
          <i className={role.icon}></i>
          {role.short}
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items
          .filter((item) => item.show)
          .map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-11 rounded-btn text-sm font-medium transition-all cursor-pointer ${
                  isActive
                    ? "bg-primary-500 text-white"
                    : "text-white/65 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-lg`}></i>
              </div>
              <span className="flex-1 whitespace-nowrap">{item.label}</span>
              {item.badge ? (
                <span className={`min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ${item.key === "orders" ? "animate-pulse" : ""}`}>
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 h-11 rounded-btn text-sm font-medium text-white/65 hover:bg-white/10 hover:text-white transition-all cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line text-lg"></i>
          </div>
          <span className="whitespace-nowrap">تسجيل الخروج</span>
        </button>
      </div>
    </>
  );
}

export default function BranchSidebar({ mobileOpen, onClose }: BranchSidebarProps) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] shrink-0 bg-foreground-900 text-white flex-col h-screen sticky top-0">
        <SidebarContent onNavigate={onClose} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
          <aside className="absolute right-0 top-0 h-full w-[240px] max-w-[80vw] bg-foreground-900 text-white flex flex-col animate-slide-in-right">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 left-4 w-8 h-8 rounded-btn flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <SidebarContent onNavigate={onClose} />
          </aside>
        </div>
      ) : null}
    </>
  );
}